import React, { ReactElement } from "react"
import { RoutingInfo } from "../../types/RoutingInfo"
import { MemberProviderSelection } from "../../types/MemberProviderSelection"
import { JourneyInfoQuestionKey } from "../../utils/RoutingInfoQuestionKey"
import { capitalizeFirstLetter, getOptionTitle, lowercaseFirstLetter } from "../../utils/AnswerUtils"
import { MostHelpfulOptions } from "../../utils/MostHelpfulOptions"
import { AdoptionPathwayChoices } from "../../utils/AdoptionPathwayChoices"
import { PreservationChoices } from "../../utils/PreservationChoices"
import { Alert, AlertIcon, Flex, List, ListItem, Text } from "@chakra-ui/react"
import { DoAnyApplyOptions } from "../../utils/DoAnyApplyOptions"
import { RoutingInfoQuestions } from "./RoutingInfoQuestions"
import { OptionTitle } from "utils/OptionTitle"
import { NumberOfChildren } from "utils/NumberOfChildren"
import { IvfTreatmentStart } from "../../utils/IvfTreatmentStartOptions"
import { PreservationCycleTestingOptions } from "../../utils/PreservationCycleTestingOptions"
import { GoalTimeframeOptions } from "../../utils/GoalTimeframeOptions"
import { SkippedAnswerDisplay } from "./Answers"
import { AmabAfabPreferenceOptions } from "../../utils/AmabAfabPreference"
import { YesNoOptions } from "utils/YesNoOptions"
import { Journeys } from "../../types/Journeys"
import { useGetAllJourneyAndJourneyStages } from "components/carrotplans/hooks/useGetJourneys"

function MultiSelectOptions({
  userSelectedOptions,
  availableOptions,
  showWarning = false
}: {
  userSelectedOptions: Array<string>
  availableOptions: Array<OptionTitle>
  showWarning?: boolean
}): ReactElement {
  return userSelectedOptions?.length > 0 ? (
    <WarningWrapper showWarning={showWarning}>
      <Flex>
        <List>
          {userSelectedOptions.map((option) => (
            <ListItem key={option}>{getOptionTitle(availableOptions, option)}</ListItem>
          ))}
        </List>
      </Flex>
    </WarningWrapper>
  ) : (
    <Text color="gray.400" fontStyle="italic">
      None Selected
    </Text>
  )
}

function WarningWrapper({ showWarning, children }: { showWarning: boolean; children: ReactElement }) {
  return showWarning ? (
    <Alert status="warning" background="transparent" padding={0}>
      <Flex>
        <AlertIcon color="yellow.500" />
        {children}
      </Flex>
    </Alert>
  ) : (
    children
  )
}

function formatGCDeliveryLocation(location): string {
  if (location?.state && location?.country) {
    return `${location?.state}, ${location?.country}`
  } else {
    return location?.state ? location?.state : location?.country
  }
}

function formatDisplayDate(dateString: string): string {
  if (dateString) {
    const date = new Date(dateString)
    return date.toLocaleString("en-US", { timeZone: "UTC", year: "numeric", month: "long", day: "numeric" })
  }
  return
}

function getJourneyDisplayName(journeys: Journeys | undefined, journeyEnumName: string | undefined): string {
  if (!journeys || !journeyEnumName) {
    return ""
  }
  const journey = journeys.find((j) => j.journeyEnumName === journeyEnumName)
  return journey?.journeyDisplayName || ""
}

function getFormattedJourneyName(journeys: Journeys | undefined, journeyEnumName: string | undefined): string {
  const displayName = getJourneyDisplayName(journeys, journeyEnumName)
  return displayName ? lowercaseFirstLetter(displayName) : ""
}

function getJourneyStageDisplayName(
  journeys: Journeys | undefined,
  journeyEnumName: string | undefined,
  journeyStageEnumName: string | undefined
): string {
  if (!journeys || !journeyEnumName || !journeyStageEnumName) {
    return ""
  }
  const journey = journeys.find((j) => j.journeyEnumName === journeyEnumName)
  if (!journey) {
    return ""
  }
  const stage = journey.stages.find((s) => s.journeyStageEnumName === journeyStageEnumName)
  return stage?.journeyStageDisplayName || ""
}

function getFormattedJourneyStageName(
  journeys: Journeys | undefined,
  journeyEnumName: string | undefined,
  journeyStageEnumName: string | undefined
): string {
  const displayName = getJourneyStageDisplayName(journeys, journeyEnumName, journeyStageEnumName)
  return displayName ? lowercaseFirstLetter(displayName) : ""
}

function getProviderDisplayName(selection: MemberProviderSelection | null | undefined): string | null {
  if (selection?.providerType === "OBGYN") {
    const name = `${selection.providerFirstName || ""} ${selection.providerLastName || ""}`.trim()
    return name || null
  }
  return selection?.providerEntityName || null
}

export default function JourneyInfoDetail({
  routingInfo,
  previousRoutingInfo
}: {
  routingInfo: RoutingInfo
  previousRoutingInfo?: RoutingInfo
}): ReactElement {
  const { data: journeys } = useGetAllJourneyAndJourneyStages()
  const skipFields = routingInfo?.skipFields
  const isStillInPrevJourneyStage =
    routingInfo?.isStillInPrevJourneyStage === "YES" || routingInfo?.isStillInPrevJourneyStage === "SENSITIVE_SCENARIO"
  const formattedPrevJourney = getFormattedJourneyName(journeys, previousRoutingInfo?.journey)
  const formattedPrevJourneyStage = getFormattedJourneyStageName(
    journeys,
    previousRoutingInfo?.journey,
    previousRoutingInfo?.journeyStage
  )

  const journeyInfo = [
    {
      questionKey: JourneyInfoQuestionKey.IsStillInPrevJourneyStage,
      hasAnswer: routingInfo?.isStillInPrevJourneyStage !== null,
      question: `Are you still in the "${formattedPrevJourneyStage}" phase of ${formattedPrevJourney}?`,
      answer: <>{getOptionTitle(YesNoOptions, routingInfo?.isStillInPrevJourneyStage)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.Journey,
      hasAnswer: routingInfo?.journey !== null,
      hideQuestion: isStillInPrevJourneyStage,
      question: "What kind of journey are you on or most interested in?",
      answer: (
        <>
          {getJourneyDisplayName(journeys, routingInfo?.journey)}
          {routingInfo?.somethingElseJourney && `: ${routingInfo?.somethingElseJourney}`}
        </>
      )
    },
    {
      questionKey: JourneyInfoQuestionKey.JourneyStage,
      hasAnswer: routingInfo?.journeyStage !== null,
      hideQuestion: isStillInPrevJourneyStage,
      question: "Where are you in this journey?",
      answer: (
        <>
          {getJourneyStageDisplayName(journeys, routingInfo?.journey, routingInfo?.journeyStage)}
          {routingInfo?.otherJourneyStage && `: ${routingInfo?.otherJourneyStage}`}
        </>
      )
    },
    {
      questionKey: JourneyInfoQuestionKey.MedicationStartDate,
      hasAnswer: routingInfo?.medicationStartDate !== null,
      question: `When did you begin taking medication for your ${
        routingInfo?.journey === "PRESERVATION" ? "Preservation" : "IVF or FET"
      } cycle?`,
      answer: <>{formatDisplayDate(routingInfo?.medicationStartDate)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.DueDate,
      hasAnswer: routingInfo?.dueDate !== null,
      question: "What is your due date?",
      answer: <>{formatDisplayDate(routingInfo?.dueDate)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.DeliveryDate,
      hasAnswer: routingInfo?.deliveryDate !== null,
      question: "When did the birth take place?",
      answer: <>{formatDisplayDate(routingInfo?.deliveryDate)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.NumberOfChildren,
      hasAnswer: routingInfo?.numberOfChildren !== null,
      question: "How many children do you have?",
      answer: <>{getOptionTitle(NumberOfChildren, routingInfo?.numberOfChildren)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.SocialDeterminantsOfHealth,
      hasAnswer: !!routingInfo?.socialDeterminantsOfHealth?.hasIssuesAffectingWellBeing,
      question: "Are there any issues affecting your well-being or access to healthcare? ",
      answer: (
        <>
          {routingInfo?.socialDeterminantsOfHealth?.hasIssuesAffectingWellBeing == "SKIP" ? (
            <SkippedAnswerDisplay />
          ) : (
            capitalizeFirstLetter(routingInfo?.socialDeterminantsOfHealth?.hasIssuesAffectingWellBeing)
          )}
        </>
      )
    },
    {
      questionKey: JourneyInfoQuestionKey.PocPrefSameBackground,
      hasAnswer: routingInfo?.pocPrefSameBackground !== null,
      question:
        "If you may be choosing a local provider, would you like help finding someone who shares your background?",
      answer: <>{capitalizeFirstLetter(routingInfo?.pocPrefSameBackground)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.MostHelpfulNow,
      hasAnswer: routingInfo?.mostHelpfulNow !== null,
      question: "What are you most interested in?",
      answer: (
        <MultiSelectOptions userSelectedOptions={routingInfo?.mostHelpfulNow} availableOptions={MostHelpfulOptions} />
      )
    },
    {
      questionKey: JourneyInfoQuestionKey.GoalTimeframe,
      hasAnswer: routingInfo?.goalTimeframe !== null && routingInfo?.goalTimeframe !== undefined,
      question: "How soon do you hope to accomplish the goal(s) you selected?",
      answer: <>{getOptionTitle(GoalTimeframeOptions, routingInfo?.goalTimeframe)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.InterestNoIvf,
      hasAnswer: routingInfo?.interestNoIvf !== null,
      question:
        "Your answers indicate that IVF may be a possibility now or in the future. Are you interested in learning about evidence-based fertility options that can help some people get pregnant without IVF?",
      answer: <>{capitalizeFirstLetter(routingInfo?.interestNoIvf)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.IvfTreatmentStart,
      hasAnswer: routingInfo?.ivfTreatmentStart !== null,
      question: "When are you starting fertility testing?",
      answer: <>{getOptionTitle(IvfTreatmentStart, routingInfo?.ivfTreatmentStart)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.AgencyAttorneyName,
      hasAnswer: routingInfo?.agencyAttorneyName !== null,
      question: "What is the name of your agency and/or attorney?",
      answer: <>{routingInfo?.agencyAttorneyName}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.ClinicAgencyAttorneyName,
      hasAnswer: routingInfo?.clinicAgencyAttorneyName !== null,
      question: "What is the name of your clinic, agency/consultancy, and/or attorney?",
      answer: <>{routingInfo?.clinicAgencyAttorneyName}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.ClinicName,
      hasAnswer:
        getProviderDisplayName(routingInfo?.memberProviderSelection) !== null || routingInfo?.clinicName !== null,
      question:
        routingInfo?.memberProviderSelection?.providerType === "OBGYN"
          ? "What is the name of your OBGYN?"
          : "What is the name of your clinic?",
      answer: <>{getProviderDisplayName(routingInfo?.memberProviderSelection) || routingInfo?.clinicName}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.AdoptionInterest,
      hasAnswer: routingInfo?.adoptionInterest !== null,
      question: "What adoption pathway are you pursuing or most interested in exploring?",
      answer: <>{getOptionTitle(AdoptionPathwayChoices, routingInfo?.adoptionInterest)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.GestationalCarrierInMind,
      hasAnswer: routingInfo?.gestationalCarrierInMind !== null,
      question: "Do you already have a gestational carrier in mind?",
      answer: <>{capitalizeFirstLetter(routingInfo?.gestationalCarrierInMind)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.GestationalCarrierDeliverLocation,
      hasAnswer:
        routingInfo?.gestationalCarrierDeliverLocation?.state !== null ||
        routingInfo?.gestationalCarrierDeliverLocation?.country !== null,
      question: "Where do you hope to have your gestational carrier deliver?",
      answer: <>{formatGCDeliveryLocation(routingInfo?.gestationalCarrierDeliverLocation)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.PreservationInterest,
      hasAnswer: routingInfo?.preservationInterest !== null,
      question: "What kind of preservation are you interested in?",
      answer: <>{getOptionTitle(PreservationChoices, routingInfo?.preservationInterest)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.DoAnyApply,
      hasAnswer: routingInfo?.doAnyApply !== null,
      question:
        routingInfo?.partnerDoAnyApply !== null || skipFields?.includes(JourneyInfoQuestionKey.PartnerDoAnyApply)
          ? "Do any of the following apply to you?"
          : "Do any of the following apply?",
      answer: (
        <MultiSelectOptions
          userSelectedOptions={routingInfo?.doAnyApply}
          availableOptions={DoAnyApplyOptions}
          showWarning={routingInfo?.doAnyApply && !routingInfo?.doAnyApply.includes("NONE")}
        />
      )
    },
    {
      questionKey: JourneyInfoQuestionKey.PartnerDoAnyApply,
      hasAnswer: routingInfo?.partnerDoAnyApply !== null,
      question: "Do any of the following apply to your partner?",
      answer: (
        <MultiSelectOptions
          userSelectedOptions={routingInfo?.partnerDoAnyApply}
          availableOptions={DoAnyApplyOptions}
          showWarning={routingInfo?.partnerDoAnyApply && !routingInfo?.partnerDoAnyApply.includes("NONE")}
        />
      )
    },
    {
      questionKey: JourneyInfoQuestionKey.EggRetrievalDate,
      hasAnswer: routingInfo?.eggRetrievalDate !== null,
      question: "When did the egg retrieval take place?",
      answer: <>{formatDisplayDate(routingInfo?.eggRetrievalDate)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.PreservationCycleTesting,
      hasAnswer: routingInfo?.preservationCycleTesting !== null,
      question: "When do you expect testing for the egg or embryo preservation cycle to take place?",
      answer: <>{getOptionTitle(PreservationCycleTestingOptions, routingInfo?.preservationCycleTesting)}</>
    },
    {
      questionKey: JourneyInfoQuestionKey.AmabAfabMedicalPreference,
      hasAnswer:
        routingInfo?.amabAfabMedicalPreference !== null && routingInfo?.amabAfabMedicalPreference !== "AUTO_SKIPPED",
      question: "Would you like to answer some health questions to help us support you better?",
      answer: <>{getOptionTitle(AmabAfabPreferenceOptions, routingInfo?.amabAfabMedicalPreference)}</>
    }
  ]

  return (
    <RoutingInfoQuestions routingInfoQuestions={journeyInfo} skipFields={skipFields} subHeader={"Journey Questions"} />
  )
}
